import type { CountryFlightStats } from "./types.ts";

export interface CountryShape {
  code: string;
  path: string;
}

export interface CountryMapEntry extends CountryShape {
  flightCount: number;
}

export interface CountryPoint {
  code: string;
  x: number;
  y: number;
}

export interface CountryPointEntry extends CountryPoint {
  flightCount: number;
}

function countryFlightCounts(countries: readonly CountryFlightStats[]): Map<string, number> {
  return new Map(
    countries.map((country) => [country.code.trim().toUpperCase(), country.flight_count]),
  );
}

export function buildCountryMapEntries(
  shapes: readonly CountryShape[],
  countries: readonly CountryFlightStats[],
): CountryMapEntry[] {
  const flightCounts = countryFlightCounts(countries);
  return shapes.map((shape) => ({
    ...shape,
    flightCount: flightCounts.get(shape.code) ?? 0,
  }));
}

export function buildCountryPointEntries(
  points: readonly CountryPoint[],
  countries: readonly CountryFlightStats[],
): CountryPointEntry[] {
  const flightCounts = countryFlightCounts(countries);
  return points.map((point) => ({
    ...point,
    flightCount: flightCounts.get(point.code) ?? 0,
  }));
}

export function countryFlag(code: string): string {
  const normalized = code.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(normalized)) return "";
  return String.fromCodePoint(
    ...Array.from(normalized, (letter) => 0x1f1e6 + letter.charCodeAt(0) - 65),
  );
}

export function countryName(code: string, locale = "en"): string {
  const normalized = code.trim().toUpperCase();
  try {
    return new Intl.DisplayNames([locale], { type: "region" }).of(normalized) ?? normalized;
  } catch {
    return normalized;
  }
}

export function flightCountLabel(count: number): string {
  return `${count} flight${count === 1 ? "" : "s"}`;
}
